/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Antigravity
 🏢 Company: Ring2All LLC
 📄 File: api/queueCallbackApi.ts (Queue Callback Report)
 🗓️ Created: 2026-01-16
 🧩 Description:
    API functions for Queue Callback Report module.
────────────────────────────────────────────────────────────────*/

import api from '../../../lib/api';
import {
    CallbackListResponse,
    CallbackActionResponse,
    CallbackFilters,
    QueueOption
} from '../types';

const BASE_URL = '/api/telephony/queue-callbacks';

/**
 * Fetch list of callbacks with filters and pagination
 */
export async function fetchCallbacks(
    page: number = 1,
    limit: number = 25,
    sortBy: string = 'created_at',
    sortOrder: 'asc' | 'desc' = 'desc',
    filters: CallbackFilters = {}
): Promise<CallbackListResponse> {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    params.append('sortBy', sortBy);
    params.append('sortOrder', sortOrder);

    if (filters.queueId) params.append('queueId', filters.queueId);
    if (filters.status) params.append('status', filters.status);
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.search) params.append('search', filters.search);

    const response = await api.get<CallbackListResponse>(`${BASE_URL}?${params.toString()}`);
    return response.data;
}

/**
 * Fetch queues for filter dropdown
 */
export async function fetchQueues(): Promise<{ success: boolean; data: QueueOption[] }> {
    const response = await api.get<{ success: boolean; data: QueueOption[] }>(`${BASE_URL}/queues`);
    return response.data;
}

/**
 * Cancel a callback
 */
export async function cancelCallback(id: number | string): Promise<CallbackActionResponse> {
    const response = await api.put<CallbackActionResponse>(`${BASE_URL}/${id}/cancel`);
    return response.data;
}

/**
 * Retry a callback
 */
export async function retryCallback(id: number | string): Promise<CallbackActionResponse> {
    const response = await api.put<CallbackActionResponse>(`${BASE_URL}/${id}/retry`);
    return response.data;
}

/**
 * Delete a callback
 */
export async function deleteCallback(id: number | string): Promise<CallbackActionResponse> {
    const response = await api.delete<CallbackActionResponse>(`${BASE_URL}/${id}`);
    return response.data;
}
